import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Rating,
  Avatar,
  Stack,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  SentimentNeutral,
  Reply as ReplyIcon,
  CheckCircle,
  VisibilityOff,
  Delete,
  Refresh,
  Flag,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import eventsApi from '../services/eventsApi';
import reviewsApi from '../services/reviewsApi';

const MARK_COLOR = { good: '#27ae60', neutral: '#7f8c8d', bad: '#e74c3c' };
const MARK_ICON = {
  good: <ThumbUp sx={{ fontSize: 14 }} />,
  neutral: <SentimentNeutral sx={{ fontSize: 14 }} />,
  bad: <ThumbDown sx={{ fontSize: 14 }} />,
};

const formatDate = (raw) => {
  if (!raw) return '';
  try { return new Date(raw).toLocaleString(); } catch { return raw; }
};

const ReviewRow = ({ review, onApprove, onHide, onDelete, onReply, busy }) => {
  const customer = review.customer || {};
  const customerName = customer.full_name || `${customer.firstname || ''} ${customer.lastname || ''}`.trim() || 'Customer';
  const initials = customerName.split(' ').filter(Boolean).map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const markColor = MARK_COLOR[review.mark] || MARK_COLOR.neutral;

  return (
    <Card sx={{ mb: 1.5, borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box display="flex" gap={1.5} alignItems="flex-start" flex={1} minWidth={0}>
            <Avatar sx={{ bgcolor: '#87003A' }}>{initials || '?'}</Avatar>
            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="subtitle2" fontWeight={700}>{customerName}</Typography>
                <Chip
                  size="small"
                  icon={MARK_ICON[review.mark]}
                  label={(review.mark || 'neutral').toUpperCase()}
                  sx={{ bgcolor: `${markColor}22`, color: markColor, fontWeight: 700, height: 22 }}
                />
                {!review.is_approved && (
                  <Chip size="small" label="Hidden" sx={{ bgcolor: '#fff3cd', color: '#856404', fontWeight: 700, height: 22 }} />
                )}
                {review.is_flagged && (
                  <Chip size="small" icon={<Flag sx={{ fontSize: 14 }} />} label="Flagged" sx={{ bgcolor: '#ffe0e0', color: '#c0392b', fontWeight: 700, height: 22 }} />
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={1} mt={0.25}>
                <Rating value={review.rating} readOnly size="small" />
                <Typography variant="caption" color="text.secondary">{formatDate(review.created_at)}</Typography>
                {review.event_name && (
                  <Typography variant="caption" color="text.secondary">· {review.event_name}</Typography>
                )}
              </Box>
              {review.title && <Typography variant="body2" fontWeight={600} mt={1}>{review.title}</Typography>}
              {review.comment && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {review.comment}
                </Typography>
              )}
              {review.flag_reason && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                  <Typography variant="caption">Flag reason: {review.flag_reason}</Typography>
                </Alert>
              )}
              {review.staff_reply && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(135,0,58,0.05)', borderLeft: '3px solid #87003A', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="#87003A">Your reply · {formatDate(review.staff_reply_at)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>{review.staff_reply}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
            <Tooltip title={review.is_approved ? 'Hide review' : 'Approve review'}>
              <span>
                {review.is_approved ? (
                  <IconButton size="small" onClick={() => onHide(review)} disabled={busy === `hide-${review.id}`}>
                    <VisibilityOff fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton size="small" color="success" onClick={() => onApprove(review)} disabled={busy === `approve-${review.id}`}>
                    <CheckCircle fontSize="small" />
                  </IconButton>
                )}
              </span>
            </Tooltip>
            <Tooltip title="Reply">
              <IconButton size="small" onClick={() => onReply(review)} disabled={busy === `reply-${review.id}`}>
                <ReplyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => onDelete(review)} disabled={busy === `delete-${review.id}`}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const Reviews = () => {
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filterMark, setFilterMark] = useState('');
  const [filterApproved, setFilterApproved] = useState('');
  const [filterFlagged, setFilterFlagged] = useState('');

  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadReviews = async (cid = companyId) => {
    if (!cid) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterMark) params.mark = filterMark;
      if (filterApproved) params.approved = filterApproved;
      if (filterFlagged) params.flagged = filterFlagged;
      const data = await reviewsApi.listForCompany(cid, params);
      setReviews(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || e.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cid = await eventsApi.getCompanyId(user);
        if (cancelled) return;
        setCompanyId(cid);
        await loadReviews(cid);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load company');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (companyId) loadReviews(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMark, filterApproved, filterFlagged]);

  const counts = useMemo(() => {
    const c = { total: reviews.length, good: 0, neutral: 0, bad: 0, flagged: 0, hidden: 0 };
    let sum = 0;
    reviews.forEach((r) => {
      sum += r.rating || 0;
      if (r.mark === 'good') c.good += 1;
      else if (r.mark === 'bad') c.bad += 1;
      else c.neutral += 1;
      if (r.is_flagged) c.flagged += 1;
      if (!r.is_approved) c.hidden += 1;
    });
    c.avg = c.total ? (sum / c.total).toFixed(2) : '0.00';
    return c;
  }, [reviews]);

  const handleApprove = async (review) => {
    setBusy(`approve-${review.id}`);
    try {
      const updated = await reviewsApi.approve(review.id);
      setReviews((prev) => prev.map((r) => r.id === review.id ? updated : r));
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const handleHide = async (review) => {
    setBusy(`hide-${review.id}`);
    try {
      const updated = await reviewsApi.hide(review.id);
      setReviews((prev) => prev.map((r) => r.id === review.id ? updated : r));
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(`delete-${deleteTarget.id}`);
    try {
      await reviewsApi.remove(deleteTarget.id);
      setReviews((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const handleSendReply = async () => {
    if (!replyTarget) return;
    setBusy(`reply-${replyTarget.id}`);
    try {
      const updated = await reviewsApi.reply(replyTarget.id, replyText);
      setReviews((prev) => prev.map((r) => r.id === replyTarget.id ? updated : r));
      setReplyTarget(null);
      setReplyText('');
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, md: 2 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Reviews</Typography>
          <Typography variant="body2" color="text.secondary">
            Moderate customer feedback for your events
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={() => loadReviews()} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Total', value: counts.total },
          { label: 'Avg ★', value: counts.avg },
          { label: 'Good', value: counts.good, color: MARK_COLOR.good },
          { label: 'Bad', value: counts.bad, color: MARK_COLOR.bad },
          { label: 'Flagged', value: counts.flagged, color: '#c0392b' },
          { label: 'Hidden', value: counts.hidden, color: '#856404' },
        ].map((kpi) => (
          <Grid item xs={6} sm={4} md={2} key={kpi.label}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: kpi.color || 'text.primary' }}>
                  {kpi.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Mark</InputLabel>
              <Select value={filterMark} label="Mark" onChange={(e) => setFilterMark(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="good">Good</MenuItem>
                <MenuItem value="neutral">Neutral</MenuItem>
                <MenuItem value="bad">Bad</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterApproved} label="Status" onChange={(e) => setFilterApproved(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Approved</MenuItem>
                <MenuItem value="false">Hidden</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Flagged</InputLabel>
              <Select value={filterFlagged} label="Flagged" onChange={(e) => setFilterFlagged(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Flagged</MenuItem>
                <MenuItem value="false">Not flagged</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : reviews.length === 0 ? (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">No reviews match the current filters.</Typography>
          </CardContent>
        </Card>
      ) : (
        reviews.map((review) => (
          <ReviewRow
            key={review.id}
            review={review}
            onApprove={handleApprove}
            onHide={handleHide}
            onDelete={(r) => setDeleteTarget(r)}
            onReply={(r) => { setReplyTarget(r); setReplyText(r.staff_reply || ''); }}
            busy={busy}
          />
        ))
      )}

      {/* Reply dialog */}
      <Dialog open={Boolean(replyTarget)} onClose={() => { setReplyTarget(null); setReplyText(''); }} fullWidth maxWidth="sm">
        <DialogTitle>Reply to review</DialogTitle>
        <DialogContent>
          {replyTarget && (
            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">{replyTarget.event_name}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{replyTarget.comment}</Typography>
              <Divider sx={{ my: 1.5 }} />
            </Box>
          )}
          <TextField
            label="Your reply"
            autoFocus
            multiline
            minRows={3}
            fullWidth
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReplyTarget(null); setReplyText(''); }}>Cancel</Button>
          <Button onClick={handleSendReply} variant="contained" sx={{ bgcolor: '#87003A', '&:hover': { bgcolor: '#3d000f' } }}>
            {replyTarget?.staff_reply ? 'Update reply' : 'Send reply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete review?</DialogTitle>
        <DialogContent>
          <Typography>This permanently removes the review for everyone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Reviews;
